/* eslint-disable no-param-reassign */
import React from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'utils/modifiedTranslation';
import Button from '@material-ui/core/Button';
import CheckBoxIcon from '@material-ui/icons/CheckBox';
import CheckIcon from '@material-ui/icons/Check';
import Grid from '@material-ui/core/Grid';
import { useSelector } from 'react-redux';
import { fixedOptionsValues } from 'components/Questionnaire/constants/fixedOptionsValues';
import { mapFormToConditionProfile } from 'components/Questionnaire/functions/mapFormToConditionProfile';
import getAutosuggestHighlightParts from 'functions/getAutosuggestHighlightParts';
import { checkConditions } from 'modules/questionnaire/checkConditions/checkConditions';
import useStyles from './styles';

/**
 * An option to select. Available to choose one or several options
 */
const OptionSelect = React.forwardRef((props, forwardedRef) => {
  const {
    form = {},
    isMultipleSelection,
    onChange,
    options,
    splitIntoColumns,
    gridClassName,
    rootComponent,
    listItemComponent,
    rootComponentProps = {},
    rootClassName,
    buttonClasses,
    customCheckedOptionClass,
    customFixedOptionsClass,
    customFixedCheckedOptionsClass,
    customOptionClass,
    showCheckBoxIcon,
    scrollToOption,
    inputValue,
    disabled,
    small,
  } = props;
  const { t } = useTranslation();
  const classes = useStyles({ small });
  const values = Array.isArray(props.values) ? props.values : [props.values];
  const allQuestions = useSelector((state) => state.questionnaire.allQuestions);

  const checkIsOptionSelected = (() => {
    const selectedOptionValues = new Set(values.map(({ value }) => value));
    return (option) => selectedOptionValues.has(option?.value);
  })();

  const handleChange = (event) => {
    event.preventDefault();
    const selectedOptions = [];
    const selectedOption = options.find((option) => option.value === event.currentTarget.dataset.value);

    if (selectedOption.onChange) {
      selectedOption.onChange();
      return;
    }

    if (!isMultipleSelection) {
      selectedOptions.push(selectedOption);
    } else {
      selectedOptions.push(...values);
      const selectedOptionIndex = selectedOptions.findIndex((option) => option.value === selectedOption.value);

      if (selectedOptionIndex === -1) {
        selectedOptions.push(selectedOption);
      } else {
        selectedOptions.splice(selectedOptionIndex, 1);
      }
    }
    onChange(selectedOptions);
  };

  const conditionProfile = mapFormToConditionProfile(form, allQuestions);

  const isOptionDisabled = (option) => {
    const { disableConditions } = option;

    if (!Array.isArray(disableConditions)) {
      return false;
    }

    return checkConditions(conditionProfile, disableConditions);
  };

  const isOptionHidden = (option) => {
    const { hideConditions } = option;

    if (!Array.isArray(hideConditions)) {
      return false;
    }

    return checkConditions(conditionProfile, hideConditions);
  };

  return (
    <Grid {...rootComponentProps} container className={rootClassName} component={rootComponent}>
      {options.map((option, index) => {
        if (isOptionHidden(option)) return null;
        const isFixedOption = fixedOptionsValues.includes(option.value);
        const isOptionSelected = checkIsOptionSelected(option);
        let label = Array.isArray(option?.label)
          ? option?.label.map((l) => t(l)).join(' + ')
          : option?.label.includes(' ')
            ? option?.label : t(option?.label);
        if (inputValue && inputValue.length > 1) {
          const parts = getAutosuggestHighlightParts(label, inputValue);
          label = (
            <div>
              {parts.map((part, partsIndex) => (
                <span key={partsIndex} style={{ backgroundColor: part.highlight ? 'rgb(253,241,219)' : 'transparant' }}>
                  {part.text}
                </span>
              ))}
            </div>
          );
        }

        return (
          <Grid
            ref={(inputElement) => {
              if (forwardedRef && inputElement) {
                if (option.value === scrollToOption) {
                  if (typeof forwardedRef === 'function') {
                    forwardedRef(inputElement);
                  } else {
                    forwardedRef.current = inputElement;
                  }
                }
              }
            }}
            className={gridClassName}
            item
            key={`${index}-${option.value}`}
            xs={12}
            sm={isMultipleSelection && splitIntoColumns ? 6 : 12}
            component={listItemComponent}
          >
            <Button
              className={` 
                  ${classes.option} 
                  ${buttonClasses}
                  ${isOptionSelected
            ? isFixedOption
              ? customFixedCheckedOptionsClass || classes.fixedCheckedOptionsClass
              : customCheckedOptionClass || classes.checkedOption
            : isFixedOption
              ? customFixedOptionsClass
              : customOptionClass
          }
              `}
              data-value={option.value}
              onClick={handleChange}
              disabled={disabled || isOptionDisabled(option)}
            >
              {showCheckBoxIcon
                && (isOptionSelected ? (
                  isFixedOption ? (
                    <div className={`${classes.optionCheckBoxOutline} ${classes.fixedOptionCheckBoxChecked}`}>
                      <CheckIcon />
                    </div>
                  ) : <CheckBoxIcon className={classes.optionCheckBoxIcon} />
                ) : (
                  <div className={classes.optionCheckBoxOutline}>
                    <CheckIcon />
                  </div>
                ))}
              {label}
            </Button>
          </Grid>
        );
      })}
    </Grid>
  );
});

OptionSelect.propTypes = {
  /**
   * Array of options for a render
   */
  options: PropTypes.array.isRequired,
  /**
   * A flag for enabling several options selection. Works like checkbox.
   */
  isMultipleSelection: PropTypes.bool,
  /**
   * Array of selected options. Array of objects in which presents labels and values of these options. or a single object
   */
  values: PropTypes.oneOfType([PropTypes.array, PropTypes.object]).isRequired,
  /**
   * Function to call when option selected
   */
  onChange: PropTypes.func.isRequired,
  /**
   * Classname for the root div
   */
  rootClassName: PropTypes.string,
  gridClassName: PropTypes.string,
  /**
   * If the options should be divided by <Grid /> into columns using <Grid sm={6} />
   */
  splitIntoColumns: PropTypes.bool,
  showCheckBoxIcon: PropTypes.bool,
  buttonClasses: PropTypes.string,
  customOptionClass: PropTypes.string,
  customCheckedOptionClass: PropTypes.string,
  customFixedOptionsClass: PropTypes.string,
  customFixedCheckedOptionsClass: PropTypes.string,
  form: PropTypes.object,
  rootComponent: PropTypes.string,
  listItemComponent: PropTypes.string,
  inputValue: PropTypes.string,
  rootComponentProps: PropTypes.object,
  scrollToOption: PropTypes.any,
  disabled: PropTypes.bool,
  small: PropTypes.bool,
};

export default OptionSelect;
